import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Trash2,
  GripVertical,
  Palette,
  Save,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: '#64748b' },
  { id: 'contacted', label: 'Contacted', color: '#3b82f6' },
  { id: 'qualified', label: 'Qualified', color: '#8b5cf6' },
  { id: 'proposal', label: 'Proposal', color: '#f59e0b' },
  { id: 'negotiation', label: 'Negotiation', color: '#ef4444' },
  { id: 'closed_won', label: 'Closed Won', color: '#22c55e' },
  { id: 'closed_lost', label: 'Closed Lost', color: '#6b7280' }
];

const PRESET_COLORS = [
  '#64748b', '#3b82f6', '#8b5cf6', '#ec4899', 
  '#f59e0b', '#ef4444', '#22c55e', '#06b6d4',
  '#84cc16', '#f97316', '#14b8a6', '#6366f1'
];

export const StageManager = ({ 
  isOpen, 
  onClose, 
  stages = DEFAULT_STAGES, 
  onSave 
}) => {
  const [localStages, setLocalStages] = useState(stages);
  const [editingId, setEditingId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalStages(stages);
    setHasChanges(false);
  }, [stages, isOpen]);

  const handleAddStage = () => {
    const newStage = {
      id: `stage_${Date.now()}`,
      label: 'New Stage',
      color: PRESET_COLORS[localStages.length % PRESET_COLORS.length]
    };
    setLocalStages([...localStages, newStage]);
    setEditingId(newStage.id);
    setHasChanges(true);
  };

  const handleDeleteStage = (stageId) => {
    // Don't allow deleting if less than 2 stages
    if (localStages.length <= 2) {
      toast.error('Minimum 2 stages required');
      return;
    }
    
    setLocalStages(localStages.filter(s => s.id !== stageId));
    setHasChanges(true);
  };

  const handleUpdateStage = (stageId, updates) => {
    setLocalStages(localStages.map(s => 
      s.id === stageId ? { ...s, ...updates } : s
    ));
    setHasChanges(true);
  };

  const handleReorder = (fromIndex, toIndex) => {
    const newStages = [...localStages];
    const [removed] = newStages.splice(fromIndex, 1);
    newStages.splice(toIndex, 0, removed);
    setLocalStages(newStages);
    setHasChanges(true);
  };

  const handleSave = () => {
    // Validate stages
    const emptyLabels = localStages.filter(s => !s.label.trim());
    if (emptyLabels.length > 0) {
      toast.error('All stages must have a name');
      return;
    }

    // Ensure unique IDs
    const uniqueStages = localStages.map((s, idx) => ({
      ...s,
      id: s.id || `stage_${idx}_${Date.now()}`
    }));

    onSave(uniqueStages);
    toast.success('Pipeline stages saved');
    onClose();
  };

  const handleReset = () => {
    setLocalStages(DEFAULT_STAGES);
    setHasChanges(true);
    toast.info('Reset to default stages');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-bold text-white">Manage Pipeline Stages</h3>
            <p className="text-sm text-slate-400">Customize your deal stages</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="space-y-2">
            {localStages.map((stage, index) => (
              <motion.div
                key={stage.id}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center space-x-2 p-2 bg-white/5 rounded-lg group"
              >
                {/* Drag Handle */}
                <button
                  className="p-1 cursor-grab text-slate-500 hover:text-slate-300"
                  title="Drag to reorder"
                >
                  <GripVertical className="w-4 h-4" />
                </button>

                {/* Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowColorPicker(showColorPicker === stage.id ? null : stage.id)}
                    className="w-6 h-6 rounded-full border-2 border-white/20 hover:border-white/40 transition-colors"
                    style={{ backgroundColor: stage.color }}
                    title="Change color"
                  />
                  
                  <AnimatePresence>
                    {showColorPicker === stage.id && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="absolute left-0 top-8 z-50 p-2 bg-slate-700 rounded-lg shadow-xl border border-white/10"
                      >
                        <div className="grid grid-cols-4 gap-1">
                          {PRESET_COLORS.map((color) => (
                            <button
                              key={color}
                              onClick={() => {
                                handleUpdateStage(stage.id, { color });
                                setShowColorPicker(null);
                              }}
                              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                                stage.color === color ? 'border-white' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Stage Name */}
                <input
                  type="text"
                  value={stage.label}
                  onChange={(e) => handleUpdateStage(stage.id, { label: e.target.value })}
                  onFocus={() => setEditingId(stage.id)}
                  onBlur={() => setEditingId(null)}
                  className={`
                    flex-1 px-3 py-1.5 bg-transparent rounded-lg text-white text-sm
                    border border-transparent focus:border-neon-cyan/50 focus:outline-none
                    ${editingId === stage.id ? 'bg-white/5' : ''}
                  `}
                  placeholder="Stage name"
                />

                {/* Order Number */}
                <span className="text-xs text-slate-500 w-6 text-center">
                  #{index + 1}
                </span>

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteStage(stage.id)}
                  className="p-1.5 rounded-lg hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                  title="Delete stage"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>

          {/* Add Stage Button */}
          <button
            onClick={handleAddStage}
            className="w-full mt-4 py-2 border-2 border-dashed border-white/10 rounded-lg text-slate-400 hover:text-white hover:border-neon-cyan/30 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Stage</span>
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-slate-800/50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Reset to Default
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 transition-colors
                  ${hasChanges 
                    ? 'bg-neon-cyan text-slate-900 hover:bg-neon-cyan/80' 
                    : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }
                `}
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StageManager;
